import React, { useContext } from 'react';
import { Element } from 'react-scroll';
import Link from 'next/link';
import Alert from '@common/alert';
import { useToast } from '@common/toast';
import ImgSlide from '@components/events/eventChallenge/imgSlide';
import { EventChallengeModalContext } from '@components/events/eventChallenge/modalsWrap';
import { useDownloadTalentPoolResume, useEventsTalentPoolAccaTestApply } from '@repository/event/useEvent';
import { TalentPoolEventDto } from '@domain/rs/event/TalentPoolEventRs';
import { MissionType } from '@domain/constant/event/marketing/MissionType';
import { RouteUrl } from '@utils/routeUtil';
import FileUtil from '@utils/fileUtil';

import classnames from 'classnames/bind';
import styles from './challengeMission2.module.scss';

const cx = classnames.bind(styles);

interface IChallengeMission2Props {
  isLogin: boolean;
  talentPoolEventData?: TalentPoolEventDto;
  onReFetch: () => void;
}

const ChallengeMission2 = ({ isLogin, talentPoolEventData, onReFetch }: IChallengeMission2Props) => {
  const Toast = useToast();
  const { setModalState } = useContext(EventChallengeModalContext);

  //역량검사 응시 여부 상태
  const isTalentPoolAccTest = !!talentPoolEventData?.isTalentPoolAccTest;
  //역량검사 이벤트 응모 여부
  const isTalentPoolAccTestEventParticipation = !!talentPoolEventData?.isTalentPoolAccTestEventParticipation;

  const accaTestButtonText = isTalentPoolAccTest ? '역량검사 재응시하기' : '역량검사 응시하기';
  const eventButtonText = isTalentPoolAccTestEventParticipation ? '응모 완료' : '이벤트 응모하고 혜택 바로 받기 ';

  const { mutate: downloadTalentPoolResumeMutate } = useDownloadTalentPoolResume(
    (data) => {
      FileUtil.fileOpenToPdf(data);

      Alert({
        title: '혜택 지급 완료',
        desc: (
          <>
            합격자 이력서를 토대로
            <br />
            프로필을 업데이트 해보세요.
          </>
        ),
        dimmed: true,
        onClickBgClose: true,
      });
      return;
    },
    () => {
      Toast({ type: 'error', iconType: 'info', content: '역량검사 응시를 완료해야 다운로드가 가능합니다.' });
      return;
    },
  );

  const { mutate: talentPoolAccaTestApplyMutate } = useEventsTalentPoolAccaTestApply(
    () => {
      Alert({
        title: '혜택 지급 완료',
        desc: (
          <>
            1. 핸드폰 번호로 발송된 기프티콘을 확인해주세요
            <br />
            2. 합격자 이력서 모음ZIP은 다운로드 가능해요!
          </>
        ),
        dimmed: true,
        onClickBgClose: true,
      });
      onReFetch();
    },
    (err) => {
      switch (err?.response?.data.errorCode) {
        case 'B904':
        case 'B905':
          setModalState('MARKETING_POLICY_AGREE');
          return;
        case 'B998':
          Alert({
            title: '혜택 지급 완료',
            desc: (
              <>
                1. 선착순 혜택은 마감되었습니다.
                <br />
                2. 합격자 이력서 모음 ZIP은 다운로드 가능해요!
              </>
            ),
            dimmed: true,
            onClickBgClose: true,
          });
          return;
        case 'B907':
          Toast({
            type: 'error',
            iconType: 'info',
            content: '이벤트 응모 대상이 아닙니다. 이력서 모음ZIP은 다운로드 가능해요!',
          });
          return;
        default:
          Toast({ type: 'error', iconType: 'info', content: err?.response?.data?.message });
      }
    },
  );

  const handleTalentPoolAccaTestApply = () => {
    if (!isLogin) {
      setModalState('LOGIN');
      return;
    } else if (isTalentPoolAccTestEventParticipation) {
      //역량검사 응모 완료 했을 경우
      Alert({
        title: '혜택 지급 완료!',
        desc: '추첨 이벤트에 참여해보세요.',
        dimmed: true,
        onClickBgClose: true,
      });
      return;
    } else if (!isTalentPoolAccTest) {
      //역량검사 미응시 일경우
      Toast({ type: 'error', iconType: 'info', content: '역량검사 응시를 완료해야 응모가 가능합니다.' });
      return;
    }

    talentPoolAccaTestApplyMutate();
  };

  const handleDownloadResumeFile = () => {
    if (!isLogin) {
      setModalState('LOGIN');
      return;
    }

    // 자료 준비중으로 주석 처리
    downloadTalentPoolResumeMutate();
  };

  // TODO  move로 접근시 move 관련 query만 없어지도록 수정 필요
  // useMoveToComponentByQuery(MissionType.MISSION_2, query.move === 'accTest', true);

  return (
    <Element name={MissionType.MISSION_2} className={cx('wrap')} id={cx('mission2')}>
      <div className={cx('inner')}>
        <div className={cx('sectionTitleArea')}>
          <strong className={cx('sectionTitle')}>챌린지 2 · 역량검사 응시하기</strong>
        </div>

        {/* 미션 영역 */}
        <div className={cx('missionArea')}>
          <div className={cx('missionInner')}>
            <ImgSlide>
              <div className={cx('sliderItem', 'type1')} />
              <div className={cx('sliderItem', 'type2')} />
              <div className={cx('sliderItem', 'type3')} />
            </ImgSlide>
          </div>
          <div className={cx('btnArea')}>
            <Link href={RouteUrl.ACCA_TEST_LIST} legacyBehavior>
              <a className={cx('btnEvent', { outLine: isTalentPoolAccTest }, 'eventBtn_testGo')} role="button">
                {accaTestButtonText}
              </a>
            </Link>
          </div>
        </div>

        {/* 혜책 영역 */}
        <div className={cx('benefitArea')}>
          <div className={cx('benefitInner')}>
            <div className={cx('benefitWrap', 'type1')}>
              <div className={cx('textArea')}>
                <em className={cx('benefitTitle')}>
                  <span className={cx('benefitText')}>혜택 1</span>
                  {` `}
                  합격자이력서모음.zip
                </em>
                <p className={cx('benefitDesc')}>
                  인사담당자&합격자의 작성 Tip 포함
                  <br />
                  15개 직무별 금손 선배들의 합격 이력서 제공!
                </p>
              </div>

              {/* 합격자 이력서 다운로드 버튼 */}
              <a className={cx('btnDownload', 'eventBtn_zip')} role="button" onClick={handleDownloadResumeFile}>
                직무별 합격자 이력서 모음.ZIP 받기
              </a>
            </div>
          </div>
          <div className={cx('benefitInner')}>
            <div className={cx('benefitWrap', 'type2')}>
              <div className={cx('textArea')}>
                <em className={cx('benefitTitle')}>
                  <span className={cx('benefitText')}>혜택 2</span>
                  <br />내 역량 진단
                </em>
                <p className={cx('benefitDesc')}>
                  진단 결과표를 면접 준비에
                  <br />
                  활용할 수 있어요!
                </p>
              </div>
            </div>
            <div className={cx('benefitWrap', 'type3')}>
              <div className={cx('textArea')}>
                <em className={cx('benefitTitle')}>
                  <span className={cx('benefitText')}>혜택 3</span>
                  <br />
                  네이버페이
                </em>
                <p className={cx('benefitDesc')}>10,000P</p>
              </div>
            </div>
          </div>
        </div>

        {/* 응모하기 버튼 영역 */}
        <a
          className={cx(
            'btnEvent',
            {
              disabled: isTalentPoolAccTestEventParticipation,
            },
            'eventBtn_eventApply2',
          )}
          role="button"
          onClick={handleTalentPoolAccaTestApply}
        >
          {eventButtonText}
        </a>

        {/* 알림 텍스트 영역 */}
        <p className={cx('notice')}>
          - 역량검사 응시 챌린지 완료 후 [응모하기] 버튼을 누르면 이벤트 응모가 가능합니다.
          <br />
          - 네이버페이: 응모 후 즉시 회원가입 정보의 핸드폰 번호로 기프티콘이 제공됩니다. (소진 시 마감)
          <br />
          - 이벤트 시작일 이전에 이미 역량검사를 응시 완료한 회원은 [합격자 이력서 모음 ZIP]만 다운받을 수 있습니다.
          <br />
          - 네이버페이 선착순 1,000명이 소진되더라도 [합격자 이력서 모음 ZIP]은 다운 받을 수 있습니다.
          <br />- 중복 참여 불가합니다. (핸드폰 번호 당 1번 지급)
        </p>
      </div>
    </Element>
  );
};

export default ChallengeMission2;
